import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { ComponentDataTable, DefaultModal, Loader, SimpleCenteredModal } from '../../components';
import {
  closeApiPayrollProcessRequest,
  deleteApiPayrollProcessRequest,
  exportPayrollProcessRequest,
  indexApiPayrollRequest,
  massiveDestroyApiPayrollProcessRequest,
  payrollProcessReloadRequest,
  payrollProcessVerifyCompanyDataRequest
} from '../../requests/payrollProcesses';
import columns from './ColumnsPayroll';
import { sendAlert } from '../../actions/utils';
import massiveActions from '../RemunerationProcesses/AdvancePayments/Datatables/massiveActions';
import { downloadFile } from '../../services/utils';
import ModalBodyDifferenceErrors from './ModalBodyDifferenceErrors';

const defaultModalState = {
  title: '',
  body: null,
  show: false
}

const RemunerationProcessDataTable = ({ moreData, setMoreData, customParams, setOnRequest, onRequest }) => {
  const [modalAction, setModalAction] = useState(() => null);
  const [modalBody, setModalBody] = useState('');
  const [modalItem, setModalItem] = useState({});
  const [amount, setAmount] = useState(0);
  const [modalShow, setModalShow] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [simpleModalState, setSimpleModalState] = useState(defaultModalState);
  const [selectedRows, setSelectedRows] = useState([]);
  const [clearSelectedRows, setClearSelectedRows] = useState(false);
  const [payrollProcesses, setPayrollProcesses] = useState([]);
  const [selectedCount, setSelectedCount] = useState('');
  const history = useHistory();
  const dispatch = useDispatch();

  const handleSelectedRows = item => {
    setSelectedRows(item.selectedRows);
    setSelectedCount(item.selectedCount);
  };

  const handleRequest = async params => {
    setOnRequest(true);
    setClearSelectedRows(false);
    indexApiPayrollRequest({
      dispatch,
      params: {
        ...customParams,
        ...params
      },
      successCallback: response => {
        setOnRequest(false);
        setPayrollProcesses(response.data.data);
        setAmount(response?.data?.metadata?.amount);
      },
      callback: () => {
        setOnRequest(false);
        setClearSelectedRows(true);
      }
    });
  };

  const handleModalClose = () => {
    setModalShow(false);
  };

  const handleSuccess = message => {
    dispatch(sendAlert({ kind: 'success', message }));
    setMoreData(!moreData);
  };

  const removePayrollProcess = item => {
    setOnRequest(true);
    deleteApiPayrollProcessRequest(item.id, {
      dispatch,
      successCallback: () => handleSuccess('Proceso Eliminado Correctamente'),
      callback: () => {
        handleModalClose();
        setOnRequest(false);
      }
    });
  };

  const closePayrollProcess = item => {
    setOnRequest(true);
    closeApiPayrollProcessRequest(item.id, {
      dispatch,
      successCallback: () => handleSuccess('Proceso Cerrado Correctamente'),
      callback: () => {
        handleModalClose();
        setOnRequest(false);
      }
    });
  };

  const massiveDestroy = () => {
    const payrollProcessIds = selectedRows.map(payroll => payroll.id);

    setOnRequest(true);
    setClearSelectedRows(false);
    massiveDestroyApiPayrollProcessRequest({
      dispatch,
      params: {
        ids: payrollProcessIds
      },
      successCallback: () => handleSuccess('Procesos Eliminados Correctamente'),
      callback: () => {
        handleModalClose();
        setOnRequest(false);
        setClearSelectedRows(true);
        setMoreData(!moreData);
      }
    });
  };

  const handleFailureRequest = error => {
    dispatch(sendAlert({ kind: 'error', message: error.response?.data?.message }));
    setOnRequest(false);
  };

  const massiveExport = () => {
    const payrollProcessIds = selectedRows.map(payroll => payroll.id);
    setOnRequest(true);
    exportPayrollProcessRequest({
      dispatch,
      params: {
        filter_by_ids: payrollProcessIds,
        ...customParams
      },
      successCallback: response => {
        setModalShow(false);
        downloadFile(response);
        setOnRequest(false);
      },
      failureCallback: handleFailureRequest
    });
  };

  const handleModalCloseOnUpdate = (item) => {
    setSimpleModalState(state => ({ ...state, show: false }));
    history.push({ pathname: `/remuneration_processes/${item.id}/edit`, state: { item, onlyShow: false } });
  };

  const handleModalDifferenceAccept = async (item) => {
    setSimpleModalState({
      title: 'Procesando...',
      body: <Loader />,
      show: true
    });
    const updateResult = await new Promise(resolve => {
      payrollProcessReloadRequest(item.id, {
        params: {
          update_company_data: true,
          full_update: true
        },
        dispatch,
        successCallback: () => {
          resolve(true);
        }
      });
    });

    if (updateResult) {
      setSimpleModalState(defaultModalState);
      handleModalCloseOnUpdate(item);
    }
  };

  const executeEditAction = async (item) => {
    const modalDifferences = await new Promise(resolve => {
      payrollProcessVerifyCompanyDataRequest(item.id, {
        dispatch,
        successCallback: ({ data }) => {
          resolve(data);
        }
      });
    });
    if (modalDifferences?.has_any_difference) {
      setModalItem(item);
      setSimpleModalState({
        title: 'Atención',
        titleBtn: 'Reprocesar con nuevos datos',
        cancelBtn: 'Continuar sin procesar',
        show: true,
        body: (
          <ModalBodyDifferenceErrors
            companyErrors={modalDifferences.company}
            previredErrors={modalDifferences.previred}
            siitaxErrors={modalDifferences.sii_tax}
            acceptUpdate={handleModalDifferenceAccept}
            cancelUpdate={handleModalCloseOnUpdate}
            payrollProcess={item}
          />
        )
      });

      return;
    }
    history.push({ pathname: `/remuneration_processes/${item.id}/edit`, state: { item, onlyShow: false } });
  };

  const handlePayrollProcessEdit = async (item) => {
    await executeEditAction(item);
  };

  const handleButtonClick = (item, action) => {
    switch (action) {
      case 'show':
        history.push({ pathname: `/remuneration_processes/${item.id}/edit`, state: { item, onlyShow: true } });
        break;
      case 'edit':
        handlePayrollProcessEdit(item);
        break;
      case 'destroy':
        setModalTitle('Eliminar Proceso de Remuneración');
        setModalShow(true);
        setModalBody('¿Estás seguro que deseas eliminar este mes de proceso?');
        setModalAction(() => removePayrollProcess);
        setModalItem(item);
        break;
      case 'close':
        setModalTitle('Cerrar Proceso de Remuneración');
        setModalShow(true);
        setModalBody('¿Estás seguro que deseas cerrar este mes de proceso?');
        setModalAction(() => closePayrollProcess);
        setModalItem(item);
        break;
      default:
        // eslint-disable-next-line no-console
        console.warn('Error: Action not found');
    }
  };

  const handleClick = action => {
    switch (action) {
      case 'massive_deactivate':
        setModalTitle('Eliminar Procesos de Remuneración');
        setModalShow(true);
        setModalBody('¿Estás seguro que deseas eliminar los procesos seleccionados?');
        setModalAction(() => massiveDestroy);
        break;
      case 'massive_export':
        setModalTitle('Descargar Procesos de Remuneración');
        setModalShow(true);
        setModalBody('¿Estás seguro que deseas descargar los procesos seleccionados?');
        setModalAction(() => massiveExport);
        break;
      default:
        // eslint-disable-next-line no-console
        console.warn('Error: Action not found');
    }
  };

  return (
    <>
      <ComponentDataTable
        onRequest={onRequest}
        resourceRequest={handleRequest}
        columns={columns(handleButtonClick)}
        data={payrollProcesses}
        totalRows={amount}
        moreData={moreData}
        selectableRows
        selectedRowCount={selectedRows.selectedCount}
        clearSelectedRows={clearSelectedRows}
        onSelectedRowsChange={handleSelectedRows}
        withMassActions
        massActions={massiveActions(handleClick, true, !selectedCount)}
      />
      <DefaultModal
        title={modalTitle}
        body={modalBody}
        show={modalShow}
        handleClose={() => handleModalClose}
        handleConfirm={() => modalAction(modalItem)}
        disabled={onRequest}
        variantBtnClose="outline-info"
        variantBtnSave="primary"
      />
      <SimpleCenteredModal
        title={simpleModalState.title}
        size="md"
        body={simpleModalState.body}
        show={simpleModalState.show}
        onHide={() => setSimpleModalState(state => ({ ...state, show: false }))}
      />
    </>
  );
};

export default RemunerationProcessDataTable;
